import React, { useState } from 'react';
import Popover from '@/components/ui/Popover';
import type { IWidth } from '@/components/ui/Popover';
import { findChildByType } from '@/components/ui/ui.utils';
import { twMerge } from '@/utils';
import Tooltip from '@/components/ui/Tooltip';
import DropdownButton from './DropdownButton';

interface BaseDropdownProps {
  children: ReactChildren;
  className?: string;
  width?: IWidth;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const BaseDropdownComponent = React.forwardRef<
  React.ElementRef<typeof Tooltip.Trigger>,
  BaseDropdownProps
>(({
  children,
  className,
  width = 'default',
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}, ref) => {
  const [internalOpen, setInternalOpen] = useState(false);

  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const onOpenChange = controlledOnOpenChange || setInternalOpen;

  const anchorChild = findChildByType(children, Popover.Anchor);
  const contentChild = findChildByType(children, Popover.Content);
  const triggerChild = findChildByType(children, Popover.Trigger);

  if (!contentChild || !triggerChild) {
    throw new Error('BaseDropdown must have both BaseDropdown.Content and BaseDropdown.Trigger children');
  }

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      {anchorChild && (
        <Popover.Anchor>
          {anchorChild.props.children}
        </Popover.Anchor>
      )}
      <Popover.Trigger ref={ref} {...triggerChild.props}>
        {triggerChild.props.children}
      </Popover.Trigger>
      <Popover.Content
        align="start"
        className={twMerge(className, 'tw-font-tci', contentChild.props.className)}
        side="bottom"
        sideOffset={4}
        width={width}
      >
        {contentChild.props.children}
      </Popover.Content>
    </Popover>
  );
});

BaseDropdownComponent.displayName = 'Dropdown';

DropdownButton.displayName = 'Dropdown.Button';

Popover.Anchor.displayName = 'Dropdown.Anchor';

Popover.Content.displayName = 'Dropdown.Content';

Popover.Trigger.displayName = 'Dropdown.Trigger';

const BaseDropdown = Object.assign(BaseDropdownComponent, {
  Anchor: Popover.Anchor,
  Button: DropdownButton,
  Content: Popover.Content,
  Trigger: Popover.Trigger,
});

export default BaseDropdown;
